import React, { useEffect, useRef, useState } from 'react';
import { Checkbox } from '../../Common/Checkbox';
import { render } from 'storyblok-rich-text-react-renderer';
import { Addon, AddonConditionsDescription, RichText } from '../../../types/cms_shared_types';

interface ConditionsItemProps {
  selected: boolean;
  description: RichText;
  onChange: (value: boolean) => void;
}

const ConditionsItem: React.FC<ConditionsItemProps> = ({ selected, description, onChange }) => (
  <div className="flex flex-row items-center">
    <Checkbox selected={selected} setSelected={onChange} />
    <div className="ml-4 text-sm font-medium text-text-dark">{render(description)}</div>
  </div>
);

interface TermsAndConditionsProps {
  agreementConditionsDescription: RichText;
  addonConditionsDescriptions: AddonConditionsDescription[];
  selectedAddons: Addon[];
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
  additionalConditionsDescription?: RichText;
  initialTermsAccepted?: boolean;
}

export const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  agreementConditionsDescription,
  addonConditionsDescriptions,
  selectedAddons,
  setFieldValue,
  additionalConditionsDescription,
  initialTermsAccepted = false,
}) => {
  const [relevantAddonTACs, setRelevantAddonTACs] = useState<AddonConditionsDescription[]>([]);

  const [baseTACAccepted, setBaseTACAccepted] = useState(initialTermsAccepted);
  const [addonTACAccepted, setAddonTACAccepted] = useState<{ [key: string]: boolean }>({});
  const [additionalTACAccepted, setAdditionalTACAccepted] = useState(initialTermsAccepted);

  const setFieldValueRef = useRef(setFieldValue);
  useEffect(() => {
    setFieldValueRef.current = setFieldValue;
  }, [setFieldValue]);

  const syncAcceptedTerms = (
    baseAccepted: boolean,
    addonAccepted: { [key: string]: boolean },
    additionalAccepted: boolean,
  ) => {
    const allAddonTACsAccepted = Object.values(addonAccepted).every(Boolean);
    const additionalTermsAccepted = !additionalConditionsDescription || additionalAccepted;

    setFieldValueRef.current(
      'acceptTermsAndConditions',
      baseAccepted && allAddonTACsAccepted && additionalTermsAccepted,
    );
  };

  useEffect(() => {
    syncAcceptedTerms(initialTermsAccepted, {}, initialTermsAccepted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const selectedAddonProviderUuids = new Set(selectedAddons.map((addon) => addon.addonProvider.uuid));
    const _relevantAddonTACs = addonConditionsDescriptions.filter((item) =>
      selectedAddonProviderUuids.has(item.addonProvider.uuid),
    );
    const nextAddonTACAccepted = _relevantAddonTACs
      .map((tac) => ({ [tac.addonProvider.uuid]: initialTermsAccepted }))
      .reduce((acc, curr) => Object.assign(acc, curr), {});

    setRelevantAddonTACs(_relevantAddonTACs);
    setAddonTACAccepted(nextAddonTACAccepted);
    syncAcceptedTerms(baseTACAccepted, nextAddonTACAccepted, additionalTACAccepted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddons, addonConditionsDescriptions]);

  const handleBaseTACChange = (accepted: boolean) => {
    setBaseTACAccepted(accepted);
    syncAcceptedTerms(accepted, addonTACAccepted, additionalTACAccepted);
  };

  const handleAddonTACChange = (providerUuid: string, accepted: boolean) => {
    const newAddonTACAccepted = { ...addonTACAccepted, [providerUuid]: accepted };
    setAddonTACAccepted(newAddonTACAccepted);
    syncAcceptedTerms(baseTACAccepted, newAddonTACAccepted, additionalTACAccepted);
  };

  const handleAdditionalTACChange = (accepted: boolean) => {
    setAdditionalTACAccepted(accepted);
    syncAcceptedTerms(baseTACAccepted, addonTACAccepted, accepted);
  };

  return (
    <div className="flex flex-col gap-2">
      <ConditionsItem
        selected={baseTACAccepted}
        description={agreementConditionsDescription}
        onChange={handleBaseTACChange}
      />
      {relevantAddonTACs?.map((item) => (
        <ConditionsItem
          key={item.addonProvider.uuid}
          selected={addonTACAccepted[item.addonProvider.uuid]}
          description={item.description}
          onChange={(accepted) => handleAddonTACChange(item.addonProvider.uuid, accepted)}
        />
      ))}
      {additionalConditionsDescription && (
        <ConditionsItem
          selected={additionalTACAccepted}
          description={additionalConditionsDescription}
          onChange={handleAdditionalTACChange}
        />
      )}
    </div>
  );
};
